import {
  Button,
  Col,
  DatePicker,
  Form,
  InputNumber,
  Modal,
  Row,
  Typography,
} from 'antd';
import type { Dayjs } from 'dayjs';
import moment from 'moment';
import React, { useCallback } from 'react';

export type FuelSurchargeBulkUpdateValues = {
  fuelSurcharge: number;
  fuelSurchargeExpiry: string | null;
};

type FormState = {
  fuelSurcharge?: number | string | null;
  fuelSurchargeExpiry?: Dayjs | null;
};

interface IProps {
  selectedCount: number;
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (values: FuelSurchargeBulkUpdateValues) => void | Promise<unknown>;
}

const hasFuelValue = (surcharge: unknown) => parseFloat(String(surcharge)) > 0;

const disablePastAndToday = (date: Dayjs | null) =>
  !!date && date.valueOf() < moment().add(1, 'days').startOf('day').valueOf();

const FuelSurchargeBulkUpdateModal: React.FC<IProps> = ({
  onClose,
  onSubmit,
  selectedCount,
  submitting,
}) => {
  const [form] = Form.useForm<FormState>();
  const fuelSurcharge = Form.useWatch('fuelSurcharge', form);
  const hasFuel = hasFuelValue(fuelSurcharge);

  const closeModal = useCallback(() => {
    if (submitting) return;
    form.resetFields();
    onClose();
  }, [form, onClose, submitting]);

  const onValuesChange = useCallback(
    (changedValues: Partial<FormState>, allValues: FormState) => {
      if ('fuelSurchargeExpiry' in changedValues) {
        form.validateFields(['fuelSurchargeExpiry']);
      }
      if (!('fuelSurcharge' in changedValues)) return;
      if (!hasFuelValue(allValues.fuelSurcharge)) {
        form.setFieldsValue({ fuelSurchargeExpiry: undefined });
        form.setFields([{ errors: [], name: 'fuelSurchargeExpiry' }]);
      }
    },
    [form]
  );

  const submitForm = useCallback(
    async (formState: FormState) => {
      const surcharge = parseFloat(String(formState.fuelSurcharge)) || 0;
      const expiry = formState.fuelSurchargeExpiry;
      try {
        await onSubmit({
          fuelSurcharge: surcharge,
          fuelSurchargeExpiry:
            hasFuelValue(surcharge) && expiry?.isValid()
              ? expiry.format('YYYY-MM-DD')
              : null,
        });
        form.resetFields();
      } catch {
        // Parent surfaces the error; keep the modal open for retry.
      }
    },
    [form, onSubmit]
  );

  return (
    <Modal
      centered
      open
      footer={null}
      maskClosable={false}
      title="Update Fuel Surcharge"
      width={480}
      onCancel={closeModal}
    >
      <Typography.Paragraph type="secondary">
        Apply these values to {selectedCount} selected carrier
        {selectedCount === 1 ? '' : 's'}.
      </Typography.Paragraph>
      <Form
        form={form}
        layout="vertical"
        onFinish={submitForm}
        onValuesChange={onValuesChange}
      >
        <Row gutter={24}>
          <Col>
            <Form.Item
              label="Fuel surcharge %"
              name="fuelSurcharge"
              rules={[{ message: 'required', required: true }]}
            >
              <InputNumber
                controls={false}
                disabled={submitting}
                style={{ width: 100 }}
              />
            </Form.Item>
          </Col>
          <Col>
            <Form.Item
              label="Expiry Date"
              name="fuelSurchargeExpiry"
              rules={[
                {
                  validator(_, value) {
                    if (!hasFuel) return Promise.resolve();
                    if (!value) return Promise.reject(new Error('required'));
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <DatePicker
                allowClear={false}
                disabled={!hasFuel || submitting}
                disabledDate={disablePastAndToday}
                format="YYYY-MM-DD"
                inputReadOnly={false}
                placeholder="YYYY-MM-DD"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24} style={{ marginTop: 16 }}>
          <Col span={12}>
            <Form.Item>
              <Button
                className="modal-close-btn"
                disabled={submitting}
                onClick={closeModal}
              >
                Cancel
              </Button>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item>
              <Button
                className="modal-save-btn"
                disabled={submitting}
                htmlType="submit"
                loading={submitting}
                type="primary"
              >
                SAVE
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default FuelSurchargeBulkUpdateModal;
